import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Youtube, Twitter, MessageCircle, Briefcase } from "lucide-react";
import { api } from "@/api";

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  x: Twitter,
  whatsapp: MessageCircle,
};

export default function Footer() {
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const socials = Object.entries(settings?.socials || {}).filter(([, url]) => url);

  return (
    <footer data-testid="site-footer" className="bg-navy-950 text-slate-300">
      <div className="container-rcs grid gap-12 py-16 lg:py-20 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-1">
          <img src="/rcs-logo.png" alt="Roy Career Solutions Private Limited" className="h-24 w-auto" data-testid="footer-logo" />
          <p className="mt-5 text-sm leading-relaxed text-slate-400">
            Building transformative learning experiences for the next generation — through specialised education brands across academics and music.
          </p>
          {socials.length > 0 && (
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              {socials.map(([key, url]) => {
                const Icon = SOCIAL_ICONS[key];
                return Icon ? (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={key} data-testid={`footer-social-${key}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors hover:border-gold hover:text-gold">
                    <Icon className="h-4 w-4" />
                  </a>
                ) : null;
              })}
            </div>
          )}
        </div>

        <nav aria-label="Company" data-testid="footer-company-links">
          <h3 className="eyebrow-light">Company</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link className="hover:text-gold transition-colors" to="/about" data-testid="footer-link-about">About</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/leadership" data-testid="footer-link-leadership">Leadership</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/journey" data-testid="footer-link-journey">Journey</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/team" data-testid="footer-link-team">Meet The Team</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/careers" data-testid="footer-link-careers">Careers</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/contact" data-testid="footer-link-contact">Contact</Link></li>
          </ul>
        </nav>

        <nav aria-label="Our Brands" data-testid="footer-brand-links">
          <h3 className="eyebrow-light">Our Brands</h3>
          <ul className="mt-5 space-y-3 text-sm">
            {brands.map((b) => (
              <li key={b.id}>
                <a className="hover:text-gold transition-colors" href={b.websiteURL} target="_blank" rel="noopener noreferrer" data-testid={`footer-brand-${b.slug}`}>
                  {b.brandName}
                </a>
              </li>
            ))}
            <li><Link className="hover:text-gold transition-colors" to="/brands" data-testid="footer-link-all-brands">All Brands</Link></li>
          </ul>
        </nav>

        <nav aria-label="Resources" data-testid="footer-resource-links">
          <h3 className="eyebrow-light">Resources</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link className="hover:text-gold transition-colors" to="/news" data-testid="footer-link-news">News & Insights</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/media" data-testid="footer-link-media">Media & Press</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/partnerships" data-testid="footer-link-partnerships">Partnerships</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/impact" data-testid="footer-link-impact">Impact</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/innovation" data-testid="footer-link-innovation">Innovation</Link></li>
          </ul>
        </nav>

        <nav aria-label="Legal" data-testid="footer-legal-links">
          <h3 className="eyebrow-light">Legal</h3>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link className="hover:text-gold transition-colors" to="/privacy" data-testid="footer-link-privacy">Privacy Policy</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/terms" data-testid="footer-link-terms">Terms of Use</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/cookies" data-testid="footer-link-cookies">Cookie Policy</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/disclaimer" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
            <li><Link className="hover:text-gold transition-colors" to="/corporate-information" data-testid="footer-link-corporate-info">Corporate Information</Link></li>
          </ul>
        </nav>
      </div>

      <div className="border-t border-white/10">
        <div className="container-rcs flex flex-col gap-2 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {company?.legalName || "Roy Career Solutions Private Limited"}. All Rights Reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/portal" data-testid="footer-employee-workspace-cta" className="btn-ivory !px-4 !py-2 !text-[11px]">
              <Briefcase className="h-3 w-3" aria-hidden /> Employee Workspace
            </Link>
            <p className="font-mono tracking-wider" data-testid="footer-cin">CIN: {company?.cin || "U85500KA2024PTC194012"}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
